// P0-7 (AB#2047) — SuperAdmin READ-ONLY "Recent bookings" panel. Opened from
// the Owners page per owner row so pilot support can locate a customer's
// booking. Renders a paged, de-identified appointment table (date/time,
// service, staff, customer name, phone, status, amount) for one of the owner's
// businesses. SuperAdmin-guarded transitively — reachable solely from the
// SuperAdmin-guarded Owners page; the backend re-authorizes regardless.
//
// Batch C (AB#2195, G-40) — the panel's one action: an open (pending /
// confirmed) booking can be cancelled with a reason through
// AdminCancelBookingModal. Nothing else here mutates appointments, customers or
// businesses.

import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAdminBusinessAppointments } from '../../../hooks/useAdminBusinessAppointments';
// AB#2052 — render appointment datetimes as the salon's wall-clock (UTC),
// never the viewer's local timezone (a 10:00Z booking must show 10:00, not
// "2:00 م" at UAE +4).
import { formatAppointmentDateTime } from '../../../lib/appointmentTime';
import type { AdminAppointmentLocateDto, AdminOwnerDto } from '../../../services/api';
import { getApiErrorMessage } from '../../../types/api-errors';
import AdminCancelBookingModal from '../Support/AdminCancelBookingModal';
import { isOpenBooking } from '../Support/bookingStatus';
import styles from './BusinessBookingsModal.module.css';

interface BusinessBookingsModalProps {
    open: boolean;
    onClose: () => void;
    owner: AdminOwnerDto;
}

const PAGE_SIZE = 10;

/** Map the wire status enum NAME to a localized label, reusing existing terms. */
function useStatusLabel() {
    const { t } = useLanguage();
    return (status: string): string => {
        switch (status) {
            case 'Pending':
            case 'Booked':
                return t('superadmin.bookings.status.booked');
            case 'Approved':
                return t('superadmin.bookings.status.approved');
            case 'Completed':
                return t('superadmin.bookings.status.completed');
            case 'Cancelled':
                return t('superadmin.bookings.status.cancelled');
            case 'NoShow':
                return t('superadmin.bookings.status.noshow');
            case 'Arrived':
                return t('superadmin.bookings.status.arrived');
            case 'Started':
                return t('superadmin.bookings.status.started');
            default:
                return status || '—';
        }
    };
}

const BusinessBookingsModal: React.FC<BusinessBookingsModalProps> = ({ open, onClose, owner }) => {
    const { t, language } = useLanguage();
    const statusLabel = useStatusLabel();

    const businesses = owner.businesses ?? [];
    const [businessId, setBusinessId] = useState<number | null>(businesses[0]?.businessId ?? null);
    const [pageNumber, setPageNumber] = useState(1);
    const [cancelTarget, setCancelTarget] = useState<AdminAppointmentLocateDto | null>(null);

    // Reset selection + page each time the modal (re)opens for an owner.
    useEffect(() => {
        if (!open) return;
        setBusinessId(businesses[0]?.businessId ?? null);
        setPageNumber(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, owner.id]);

    const query = useAdminBusinessAppointments({
        businessId: open ? businessId : null,
        pageNumber,
        pageSize: PAGE_SIZE,
    });

    const rows = query.data?.items ?? [];
    const totalCount = query.data?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const amountFmt = useMemo(
        () => new Intl.NumberFormat(language === 'ar' ? 'ar' : 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        [language],
    );
    // AB#2052 — wall-clock (UTC) date+time; the datetime is the salon's local
    // wall-clock stamped `…Z`, so it must be extracted in UTC, not converted
    // to the viewer's timezone.
    const formatDate = (iso: string): string =>
        iso ? formatAppointmentDateTime(iso, language) : '—';

    if (!open) return null;

    return (
        <>
            <div className={styles.modalOverlay} role="presentation" onClick={onClose}>
                <div
                    className={styles.modal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="admin-bookings-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.modalHeader}>
                        <h2 id="admin-bookings-title" className={styles.modalTitle}>
                            {t('superadmin.bookings.title')}
                        </h2>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label={t('superadmin.bookings.close')}
                        >
                            &times;
                        </button>
                    </div>

                    <div className={styles.modalContent}>
                        <p className={styles.ownerLine}>
                            {t('superadmin.bookings.forOwner')}{' '}
                            <span className={styles.ownerName}>{owner.fullName}</span>
                        </p>

                        {businesses.length > 1 && (
                            <div className={styles.field}>
                                <label htmlFor="admin-bookings-business" className={styles.label}>
                                    {t('superadmin.bookings.business')}
                                </label>
                                <select
                                    id="admin-bookings-business"
                                    className={styles.select}
                                    value={businessId ?? ''}
                                    onChange={(e) => {
                                        setBusinessId(Number(e.target.value));
                                        setPageNumber(1);
                                    }}
                                >
                                    {businesses.map((b) => (
                                        <option key={b.businessId} value={b.businessId}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {query.isError && (
                            <div className={styles.errorMessage}>
                                {getApiErrorMessage(query.error, t('superadmin.bookings.error'))}
                                <Button
                                    label={t('superadmin.bookings.retry')}
                                    onClick={() => query.refetch()}
                                    size="small"
                                />
                            </div>
                        )}

                        {query.isLoading && (
                            <p className={styles.stateText}>{t('superadmin.bookings.loading')}</p>
                        )}

                        {!query.isLoading && !query.isError && rows.length === 0 && (
                            <p className={styles.stateText}>{t('superadmin.bookings.empty')}</p>
                        )}

                        {!query.isError && rows.length > 0 && (
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>{t('superadmin.bookings.col.dateTime')}</th>
                                            <th>{t('superadmin.bookings.col.service')}</th>
                                            <th>{t('superadmin.bookings.col.staff')}</th>
                                            <th>{t('superadmin.bookings.col.customer')}</th>
                                            <th>{t('superadmin.bookings.col.phone')}</th>
                                            <th>{t('superadmin.bookings.col.status')}</th>
                                            <th className={styles.amountCol}>
                                                {t('superadmin.bookings.col.amount')}
                                            </th>
                                            <th>{t('superadmin.support.col.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((a) => (
                                            <tr key={a.appointmentId}>
                                                <td>{formatDate(a.appointmentDate)}</td>
                                                <td>{a.serviceName || '—'}</td>
                                                <td>{a.staffName || '—'}</td>
                                                <td>{a.customerName || '—'}</td>
                                                <td>{a.customerPhone || '—'}</td>
                                                <td>
                                                    <span className={styles.statusBadge}>
                                                        {statusLabel(a.status)}
                                                    </span>
                                                </td>
                                                <td className={styles.amountCol}>
                                                    {amountFmt.format(a.amount)}
                                                </td>
                                                <td>
                                                    {isOpenBooking(a.status) ? (
                                                        <Button
                                                            label={t('superadmin.support.action.cancel')}
                                                            onClick={() => setCancelTarget(a)}
                                                            variant="secondary"
                                                            size="small"
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!query.isError && totalPages > 1 && (
                            <div className={styles.paginationWrapper}>
                                <Pagination
                                    currentPage={pageNumber}
                                    totalPages={totalPages}
                                    onPageChange={setPageNumber}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.modalActions}>
                        <Button
                            label={t('superadmin.bookings.close')}
                            variant="secondary"
                            onClick={onClose}
                            type="button"
                        />
                    </div>
                </div>
            </div>
            {/* Outside the overlay, so a click on the cancel dialog's backdrop
                never also closes this panel. */}
            {cancelTarget && (
                <AdminCancelBookingModal
                    booking={{
                        ...cancelTarget,
                        businessName: businesses.find((b) => b.businessId === businessId)?.name,
                    }}
                    onClose={() => setCancelTarget(null)}
                />
            )}
        </>
    );
};

export default BusinessBookingsModal;
